import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Chat, GetTempoChatUnreadCountsApiResponse, ChatPage, ChatMessage } from "@acoladgroup/tempo-messaging-library";
import {
  createTempoChat,
  getTempoChat,
  getTempoChatUnreadCounts,
  getChatMessagePage,
  submitMessage,
  updateLastReadMessage,
  getTempoChatList,
} from "api/tempo-chat.api";
import { USER_TYPES } from "api/api.typing";
import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo } from "react";
import { useHubConnection } from "store/useHubConnection";
import { TempoChatListApiResponse } from "pages/tempo-chats/tempo-chat-list.typing";
import { useTranslation } from "react-i18next";
import { LanguageType } from "utils/language";
import { useUser } from "./user.query";
import { Task } from "pages/project/task/task.typing";

export const useCreateTempoChatMutation = () => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const userType = useMemo(() => user?.userType ?? USER_TYPES.supplier, [user]);

  const mutation = useMutation({
    mutationFn: (taskId: string) => {
      return createTempoChat(taskId, i18n.language as LanguageType);
    },
    onSuccess: (chatData, taskId) => {
      // Update task cache with the new tempoChatId
      queryClient.setQueryData(["tasks", i18n.language, userType], (oldData: Task[]) => {
        if (oldData) {
          return oldData.map((task) => {
            return task.id === taskId ? { ...task, tempoChatId: chatData.id } : task;
          });
        }
        return oldData;
      });
    },
  });
  return mutation;
};

export const useGetTempoChatQuery = () => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();

  return useCallback(
    async (chatId: number): Promise<Chat> => {
      return await queryClient.fetchQuery({
        queryKey: ["get-tempo-chat", chatId, i18n.language],
        queryFn: () => getTempoChat(chatId, i18n.language as LanguageType),
      });
    },
    [i18n.language, queryClient]
  );
};

export const useChatMessagePageQuery = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (chatId: number, pageNumber: number, messageToStartFrom?: number): Promise<ChatPage> => {
      return await queryClient.fetchQuery({
        queryKey: ["chat-messages", chatId, pageNumber, messageToStartFrom],
        queryFn: () => getChatMessagePage(chatId, pageNumber, messageToStartFrom),
      });
    },
    [queryClient]
  );
};

export const useSubmitMessageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<ChatMessage, AxiosError, { chatId: number; text: string }>({
    mutationFn: ({ chatId, text }) => submitMessage(chatId, text),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: ["chat-messages", chatId] });
    },
  });
};

export const useUpdateLastReadMessageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { chatId: number; lastReadMessageId: number }>({
    mutationFn: ({ chatId, lastReadMessageId }) => updateLastReadMessage(chatId, lastReadMessageId),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: ["tempo-chat-unread"] });
      queryClient.invalidateQueries({ queryKey: ["get-tempo-chat", chatId] });
      queryClient.invalidateQueries({ queryKey: ["tempo-chats"] });
    },
  });
};

export const useTempoChatUnreadCounts = () => {
  const hub = useHubConnection((state) => state.connection);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<GetTempoChatUnreadCountsApiResponse, AxiosError>({
    queryKey: ["tempo-chat-unread"],
    queryFn: getTempoChatUnreadCounts,
  });

  useEffect(() => {
    if (!hub) return;

    const handleChatMessageSubmitted = async () => {
      queryClient.invalidateQueries({ queryKey: ["tempo-chat-unread"] });
      queryClient.invalidateQueries({ queryKey: ["tempo-chats"] });
    };

    hub.off("ChatMessageSubmitted", handleChatMessageSubmitted);
    hub.on("ChatMessageSubmitted", handleChatMessageSubmitted);

    return () => {
      hub.off("ChatMessageSubmitted", handleChatMessageSubmitted);
    };
  }, [hub, queryClient]);

  function getUnreadCountPerChat(chatId: number | null) {
    if (!chatId) return 0;
    return data?.[chatId] ?? 0;
  }

  return { data, isLoading, error, getUnreadCountPerChat };
};

export const useTempoChatListQuery = () => {
  const { i18n } = useTranslation();
  const { data, isLoading, error, refetch } = useQuery<TempoChatListApiResponse, AxiosError>({
    queryKey: ["tempo-chats", i18n.language],
    queryFn: async () => getTempoChatList(i18n.language as LanguageType),
  });

  return { data, isLoading, error, refetch };
};
